import { Button, Dialog, DialogActions, DialogContent, FormControl, InputLabel, MenuItem, Box } from "@mui/material";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseCollaboratorFirms, setPlaintiffFirm } from "redux/slices/case";
import { useMemo, useState } from "react";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { P } from "theme/typography";
import PSelect from "components/PSelect";
import { stripNull } from "helpers/coll";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";

export interface PlaintiffFirmPickerDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  lensId: number;
}

const PlaintiffFirmPickerDialog = (props: PlaintiffFirmPickerDialogProps) => {
  const { open, setOpen, lensId } = props;
  const lensCase = useAppSelector(selectCase);
  const firms = useFirmsByLensId(lensId);
  const firmsMap = new Map(firms.map((f) => [f.id, f]));
  const collaboratorFirms = useAppSelector(selectCaseCollaboratorFirms);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPlaintiffFirm, setSelectedPlaintiffFirm] = useState(stripNull(lensCase.plaintiffFirm));
  const selectedPlaintiffFirmId = useMemo(() => selectedPlaintiffFirm?.id, [selectedPlaintiffFirm]);

  const snackpatch = useAppDispatchWithSnackbar();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    snackpatch(setPlaintiffFirm({ caseId: lensCase.id, plaintiffFirm: stripNull(selectedPlaintiffFirm) }), {
      successMessage: "Primary firm updated successfully",
      errorMessage: "Unable to update primary firms",
    })
      .then(() => {
        setIsSaving(false);
        setOpen(false);
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box sx={{ pb: 3 }}>
          <P variant="xlBold">Select Primary Firm</P>
        </Box>
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel id="plaintiff-firm-select">Primary Firm</InputLabel>
          <PSelect
            labelId="firm-select"
            label="Select Primary Firm"
            value={selectedPlaintiffFirmId ?? -1}
            size="small"
            onChange={(e) => {
              const newFirm = firmsMap.get(e.target.value as number);
              const namedFirm =
                newFirm !== undefined
                  ? { id: newFirm.id, name: newFirm.name, externalId: newFirm.externalId }
                  : undefined;
              setSelectedPlaintiffFirm(namedFirm);
            }}
            fullWidth
            MenuProps={{ sx: { maxHeight: "400px" } }}
            sx={{ minWidth: "200px" }}>
            <MenuItem key={-1} value={-1}>
              No Firm
            </MenuItem>
            {firms.map((firm) => (
              <MenuItem key={firm.id} value={firm.id}>
                {firm.name}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
        {collaboratorFirms.length > 0 && (
          <div>
            <div>
              <p className="text-xs pt-2">Note: Selected Primary Firm will be removed from Collaborator Firms</p>
              <p className="font-bold text-lg pt-4">Collaborator Firms</p>
            </div>
            <div className="space-y-1 max-h-64 overflow-auto">
              {collaboratorFirms.map((collaboratorFirm) => (
                <div key={collaboratorFirm.id} className="bg-gray-50 rounded px-4 py-2 text-xs text-gray-800">
                  {collaboratorFirm.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="text" disabled={isSaving} onClick={handleCancel}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" disabled={isSaving} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaintiffFirmPickerDialog;
